// 회원가입 — 약관 동의를 함께 받는다(개인정보보호법 §15)
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authApi, SocialButtons, type TermsItem } from "@taplink/web-shared";
import { ApiError } from "@taplink/web-shared";
import { useAuth } from "@taplink/web-shared";
import { TermsCheckList, requiredSatisfied, toAgreements } from "../components/TermsCheckList.tsx";

export function SignupPage() {
  const navigate = useNavigate();
  const { applyToken } = useAuth();

  const [terms, setTerms] = useState<TermsItem[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    authApi.terms().then(setTerms).catch(() => setError("약관을 불러오지 못했습니다."));
  }, []);

  const canSubmit =
    loginId.length > 0 && password.length >= 8 && requiredSatisfied(terms, checked) && !submitting;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.signup(loginId, password, toAgreements(terms, checked));
      // 가입 직후 자동 로그인
      const token = await authApi.login(loginId, password);
      await applyToken(token.accessToken);
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "회원가입에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page">
      <div className="card">
        <h1>회원가입</h1>
        {/* ⚠️ 부제는 **설정에 의존하지 않는 것**만 말한다. 예전 문구는 "소셜 계정으로
            가입하면 더 간편합니다" 였는데, 자격증명이 없으면 소셜 버튼이 사라지므로
            없는 기능을 안내하는 문구만 남았다(실측 2026-08-18 운영). 버튼은 서버가
            켠 것만 그리는데 문구가 따라오지 않으면 같은 종류의 거짓말이 된다. */}
        <p className="subtitle">이메일과 비밀번호만 있으면 바로 시작할 수 있습니다.</p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {/* 서버가 켜 둔 공급자만 그린다 — 자격증명이 없으면 버튼과 구분선이 함께 사라진다 */}
        <SocialButtons verb="시작하기" divider="또는 이메일로 가입" />

        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="loginId">이메일</label>
            <input
              id="loginId"
              type="email"
              autoComplete="username"
              required
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
            />
            <p className="hint">가입 후 인증 메일이 발송됩니다.</p>
          </div>

          <div className="field">
            <label htmlFor="password">비밀번호</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="password-hint"
            />
            <p className="hint" id="password-hint">최소 8자 이상</p>
          </div>

          <div className="field">
            <TermsCheckList
              terms={terms}
              checked={checked}
              onChange={(code, value) => setChecked((prev) => ({ ...prev, [code]: value }))}
            />
          </div>

          <button type="submit" className="btn-primary btn-block" disabled={!canSubmit}>
            {submitting ? "가입 중…" : "가입하기"}
          </button>
        </form>

        <div className="footer-links">
          <span>이미 계정이 있으신가요?</span>
          <Link to="/login">로그인</Link>
        </div>
      </div>
    </main>
  );
}
